<script setup lang="ts">
import router from "@/router";
import { removeToken } from "@/utils/token";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { User01, LogOut01 } from "utruckin-icons";
import { useUserStore } from "@/features/users/user.store";

const userStore = useUserStore();

const profileRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const signOut = () => {
  removeToken();
  router.push("/auth");
};

const onOutsideClick = (e: MouseEvent) => {
  if (!profileRef.value?.contains(e.target as Node)) {
    isOpen.value = false;
  }
};

const log = computed(() => {
  if (!userStore.userProfile?.name) return "";

  return userStore.userProfile.name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");
});

onMounted(() => {
  document.addEventListener("mousedown", onOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onOutsideClick);
});
</script>

<template>
  <div class="w-profile" ref="profileRef">
    <div class="body" :class="{ active: isOpen }" @click="toggle">
      <div class="body__icon">{{ log }}</div>
      <div class="body__title">{{ userStore.userProfile?.name }}</div>
    </div>

    <div v-if="isOpen" class="dropdown">
      <div class="dropdown__item" @click="router.push('/profile')">
        <User01 :size="16" />
        <span>Profile</span>
      </div>
      <div class="dropdown__item dropdown__item--danger" @click="signOut">
        <LogOut01 :size="16" />
        <span>Log out</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.w-profile {
  position: relative;
  cursor: pointer;
  background: var(--color-secondary-system-background);
  border-radius: 12px;

  .body {
    padding: 4px 12px 4px 4px;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
    transition: border-color 0.15s ease;

    &__icon {
      width: 32px;
      height: 32px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: var(--color-primary);
      color: var(--color-white);
      border-radius: 10px;
      font-size: 12px;
    }

    &__title {
      color: var(--color-label);
    }

    &:hover,
    &.active {
      border-color: var(--color-primary-disabled);
    }
  }

  .dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: var(--color-system-background);
    border: 1px solid var(--color-border);
    border-radius: var(--base-radius-14);
    min-width: 160px;
    z-index: 100;
    box-shadow: 0 4px 16px var(--color-shadow);

    &__item {
      display: flex;
      align-items: center;
      gap: var(--base-space-8);
      padding: 12px 10px;
      font-size: 14px;
      font-weight: 500;
      color: var(--color-label);
      cursor: pointer;
      transition: background 0.15s ease;

      &:hover {
        background: var(--color-bg-secondary);
      }

      &--danger {
        color: var(--color-system-red);

        &:hover {
          background: var(--color-red-lighter);
        }
      }

      &:not(:last-child) {
        border-bottom: 1px solid var(--color-border);
      }
    }
  }
}
</style>
